import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginInput, LoginSchema } from '@simplemon/lib';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getSession, signIn } from '@/lib/auth-client';
import { consentContinuation } from '@/lib/mcp-client';

export function LoginForm() {
  const location = useLocation();
  const continuation = consentContinuation(location.search);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.email({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe ?? true, // Default to true if not specified
      });

      const resultUser =
        (result as { data?: { user?: unknown } })?.data?.user ??
        (result as { user?: unknown })?.user;

      if (resultUser) {
        // Use hard redirect to ensure fresh session state on dashboard
        window.location.href = continuation ?? '/dashboard';
        return;
      }

      // Some environments may set cookies successfully even if the client returns a non-fatal error.
      // Verify session explicitly (bypass cookie cache) to avoid the "double click" experience.
      const session = await getSession({ query: { disableCookieCache: true } });
      if (session.data?.user) {
        // Use hard redirect to ensure fresh session state on dashboard
        window.location.href = continuation ?? '/dashboard';
        return;
      }

      if (result && (result as { error?: { message?: string } }).error) {
        const message = (result as { error?: { message?: string } }).error?.message;
        setError(message || 'Invalid email or password');
        return;
      }

      // Fallback: if no explicit error and not logged in, show a generic message
      setError('Sign-in did not complete. Please try again.');
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {continuation && (
        <p className="text-sm text-muted-foreground">
          Sign in to review the client connection. If you recover your password or create an
          account, start the connection again from your client afterward.
        </p>
      )}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="john@example.com"
            className="h-11"
            {...register('email')}
            disabled={isLoading}
            required
          />
          {errors.email && (
            <p className="text-sm font-medium text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/reset-password"
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            className="h-11"
            {...register('password')}
            disabled={isLoading}
            required
          />
          {errors.password && (
            <p className="text-sm font-medium text-destructive">{errors.password.message}</p>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="remember"
          {...register('rememberMe', {
            setValueAs: (v) => v === true || v === 'on',
          })}
          disabled={isLoading}
        />
        <Label
          htmlFor="remember"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Remember me for 30 days
        </Label>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full h-11 text-base" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign in'
        )}
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or</span>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <Link
          to="/register"
          className="font-semibold text-primary hover:text-primary/80 transition-colors"
        >
          Create an account
        </Link>
      </p>
    </form>
  );
}
